import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TopNav } from '../components/TopNav';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { GenStatusPill } from '../components/StatusPill';
import { SkeletonRow } from '../components/SkeletonRow';
import { EmptyState } from '../components/EmptyState';
import { GenerateModal } from '../modals/GenerateModal';
import { ConfirmDeleteModal } from '../modals/ConfirmDeleteModal';
import { listReports, downloadReport, deleteReport, type ReportSummary } from '../lib/api';
import { fmt } from '../lib/format';

export function Reports(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: reports, isLoading } = useQuery({ queryKey: ['reports'], queryFn: listReports });
  const [byUser, setByUser] = useState('all');
  const [status, setStatus] = useState('all');
  const [showGenerate, setShowGenerate] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ReportSummary | null>(null);

  const filtered = (reports ?? []).filter((r) => {
    if (status !== 'all' && r.status !== status) return false;
    if (byUser !== 'all' && r.user !== byUser) return false;
    return true;
  });

  const users = Array.from(new Set((reports ?? []).map((r) => r.user))).sort();

  return (
    <div className="ari-app">
      <TopNav />
      <div className="page">
        <PageHeader
          title="Reports"
          sub="History of every weekly + ad-hoc generation. Click a row to open the report detail."
          actions={
            <>
              <Button variant="secondary" icon="refresh" onClick={() => qc.invalidateQueries({ queryKey: ['reports'] })}>Refresh</Button>
              <Button variant="primary" icon="plus" onClick={() => setShowGenerate(true)}>Generate new report</Button>
            </>
          }
        />

        <div className="toolbar">
          <div className="left">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="calendar" size={14} style={{ color: 'var(--mute)' }} />
              <select className="select" style={{ minWidth: 160 }}>
                <option>Last 90 days</option>
                <option>Last 30 days</option>
                <option>All time</option>
              </select>
            </div>
            <select className="select" value={byUser} onChange={(e) => setByUser(e.target.value)} style={{ minWidth: 160 }}>
              <option value="all">Generated by · anyone</option>
              {users.map((u) => <option key={u}>{u}</option>)}
            </select>
            <select className="select" value={status} onChange={(e) => setStatus(e.target.value)} style={{ minWidth: 140 }}>
              <option value="all">Status · any</option>
              <option>Succeeded</option>
              <option>Failed</option>
              <option>Running</option>
            </select>
          </div>
          <div className="right">
            <span className="count"><strong>{filtered.length}</strong> of {reports?.length ?? 0}</span>
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', background: '#fff' }}>
          {isLoading ? (
            <table className="tbl gen">
              <thead><tr><th>Generated</th><th>Range</th><th className="num">Rows</th><th>By</th><th>Status</th><th /></tr></thead>
              <tbody>{Array.from({ length: 10 }).map((_, i) => <SkeletonRow key={i} cells={6} />)}</tbody>
            </table>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon="search"
              title="No reports match these filters"
              body="Try widening the date range or clearing the user filter."
              cta={<Button variant="secondary" onClick={() => { setByUser('all'); setStatus('all'); }}>Clear filters</Button>}
            />
          ) : (
            <table className="tbl gen">
              <thead>
                <tr>
                  <th>Generated <span className="sortarr">↓</span></th>
                  <th>Range covered</th>
                  <th className="num">Rows</th>
                  <th>Generated by</th>
                  <th>Status</th>
                  <th style={{ width: 1 }} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((g) => (
                  <tr key={g.id} onClick={() => g.status === 'Succeeded' && navigate(`/reports/${g.id}`)}>
                    <td className="d-tnum">{fmt.dateTime(g.generatedAt)}</td>
                    <td>{fmt.range(g.rangeStart, g.rangeEnd)}</td>
                    <td className="num">{g.rows ? fmt.num(g.rows) : <span className="em">—</span>}</td>
                    <td>{g.user}</td>
                    <td><GenStatusPill value={g.status} /></td>
                    <td style={{ paddingRight: 16 }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {g.status === 'Succeeded' && (
                          <>
                            <Button size="sm" variant="ghost" icon="eye" onClick={(e) => { e.stopPropagation(); navigate(`/reports/${g.id}`); }}>View</Button>
                            <Button size="sm" variant="ghost" icon="download" onClick={(e) => { e.stopPropagation(); downloadReport(g.id); }}>.xlsx</Button>
                            <Button size="sm" variant="ghost" icon="trash" onClick={(e) => { e.stopPropagation(); setPendingDelete(g); }}>{''}</Button>
                          </>
                        )}
                        {g.status === 'Failed' && (
                          <Button size="sm" variant="ghost" icon="alert">Error log</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!isLoading && filtered.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 24px', borderTop: '1px solid var(--border-1)', fontSize: 13, color: 'var(--mute)' }}>
            <span>Showing 1–{filtered.length} of {reports?.length ?? 0}</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <Button size="sm" variant="ghost" disabled icon="chevronLeft">{''}</Button>
              <Button size="sm" variant="ghost" icon="chevronRight">{''}</Button>
            </div>
          </div>
        )}
      </div>

      {showGenerate && (
        <GenerateModal onClose={() => setShowGenerate(false)} onGenerated={(id) => navigate(`/reports/${id}`)} />
      )}
      {pendingDelete && (
        <ConfirmDeleteModal
          generatedAt={pendingDelete.generatedAt}
          rows={pendingDelete.rows}
          user={pendingDelete.user}
          onClose={() => setPendingDelete(null)}
          onConfirm={async () => {
            await deleteReport(pendingDelete.id);
            setPendingDelete(null);
            qc.invalidateQueries({ queryKey: ['reports'] });
          }}
        />
      )}
    </div>
  );
}
